import cn from "classnames";
import { Button } from "components/UI/buttons/Button";
import { SpriteIcon } from "components/UI/SpriteIcon";
import filtersIcon from "public/img/icons/filters.svg";
import closeIcon from "public/img/icons/close.svg";
import { useState } from "react";
import st from "@/styles/modules/typography.module.scss";
import s from "./SamplesFilterMobileForm.module.scss";
import arrowRightIcon from "public/img/icons/arrow-right.svg";
import { FilterFormProps, FormFieldParams, getFilterFormFields } from "@/components/forms/filterFormFields";
import { Portal } from "components/shared/Portal";
import { freeMobileScroll, lockMobileScroll } from "utils/helpers";
import { FilterParam } from "@/utils/usePageFIlters";
import { Tag } from "@/components/UI/Tag";
import { WordsPagesInput } from "@/components/UI/form/WordsPagesInput";

export const SamplesFilterMobileForm = ({
  selectedValues,
  onChange,
  resultsCount,
  filterParams = { set: "basic" },
}: FilterFormProps & { resultsCount: number; filterParams?: FormFieldParams }) => {
  const [isOpen, setOpen] = useState(false);
  const [filterView, setFilterView] = useState<FilterParam | null>(null);

  const filterTabs = getFilterFormFields(filterParams);

  const hasSelectedFilters = !!Object.values(selectedValues).find((value) =>
    Array.isArray(value) ? !!value.length : !!value
  );

  const onFilterBtnClick = () => {
    lockMobileScroll();
    setOpen(true);
  };

  const onBackBtnClick = () => {
    if (filterView) {
      setFilterView(null);
    } else {
      freeMobileScroll();
      setOpen(false);
    }
  };

  const onShowResultsButtonClick = () => {
    setFilterView(null);
    freeMobileScroll();
    setOpen(false);
  };

  const handleOptionClick = (name: FilterParam, value: any) => {
    const currentSelectedValues = { ...selectedValues };

    if (currentSelectedValues[name]) {
      if (Array.isArray(currentSelectedValues[name])) {
        const oldValues = [...(currentSelectedValues[name] as (string | number)[])];
        if (oldValues.includes(value)) {
          oldValues.splice(oldValues.indexOf(value), 1);
          currentSelectedValues[name] = oldValues;
        } else {
          currentSelectedValues[name] = [...oldValues, value];
        }
      } else {
        currentSelectedValues[name] = [currentSelectedValues[name] as string | number, value];
      }
    } else {
      currentSelectedValues[name] = [value];
    }

    onChange(currentSelectedValues);
  };

  const hasSelectedPagesOrWords = !!(
    selectedValues.pagesFrom ||
    selectedValues.pagesTo ||
    selectedValues.wordsTo ||
    selectedValues.wordsFrom
  );

  const getSelectedUnitText = (word: string, from?: number, to?: number) => {
    if (from && to) return `From ${from} to ${to} ${word}${to > 1 ? "s" : ""}`;
    if (from) return `From ${from} ${word}${from > 1 ? "s" : ""}`;
    if (to) return `To ${to} ${word}${to > 1 ? "s" : ""}`;
    return "";
  };

  return (
    <>
      <div className={s.wrapper}>
        <button type="button" className={cn(st.card, s.filterBtn)} onClick={onFilterBtnClick}>
          <span>Filters</span> <SpriteIcon icon={filtersIcon} />
        </button>
      </div>

      <Portal>
        <div
          className={cn(s.overlay, {
            [s.overlayActive]: isOpen,
          })}
        >
          <div className={s.top}>
            <button className={s.backBtn} onClick={onBackBtnClick}>
              <SpriteIcon icon={arrowRightIcon} />
              Back
            </button>
            <span className={s.title}>Filters</span>
            <button
              className={cn(s.clearBtn, {
                [s.clearBtnActive]: hasSelectedFilters,
              })}
              onClick={() => onChange({})}
            >
              Clear all
            </button>
          </div>
          <div className={s.inner}>
            <div className={cn(s.filterTabs, { [s.filterTabsActive]: !filterView })}>
              {filterTabs.map((tab) => (
                <button
                  type="button"
                  key={tab.name}
                  className={s.filterInnerBtn}
                  onClick={() => setFilterView(tab.name)}
                >
                  <span>{tab.label}</span>
                  <SpriteIcon icon={arrowRightIcon} />
                </button>
              ))}
              <button type="button" className={s.filterInnerBtn} onClick={() => setFilterView("volume")}>
                <span>Volume</span>
                <SpriteIcon icon={arrowRightIcon} />
              </button>
              <div className={s.selectedTags}>
                {filterTabs.map((tab) => {
                  const targetGroup = selectedValues[tab.name];
                  return Array.isArray(targetGroup) && targetGroup.length ? (
                    <div key={tab.name} className={s.selectedTagsGroup}>
                      <p className={s.selectedTagsHeading}>{tab.label}</p>
                      {targetGroup.map((tag) => (
                        <Tag key={tag} className={s.selectedTag}>
                          <span>{tab.options.find((option) => `${option.value}` === tag)?.name}</span>
                          <button
                            type="button"
                            className={s.selectedTagClose}
                            onClick={() => handleOptionClick(tab.name, `${tag}`)}
                          >
                            <SpriteIcon icon={closeIcon} />
                          </button>
                        </Tag>
                      ))}
                    </div>
                  ) : null;
                })}
                {hasSelectedPagesOrWords && (
                  <div className={s.selectedTagsGroup}>
                    <p className={s.selectedTagsHeading}>Volume</p>
                    {!!(selectedValues.pagesFrom || selectedValues.pagesTo) && (
                      <Tag className={s.selectedTag}>
                        <span>
                          {getSelectedUnitText(
                            "page",
                            Number(selectedValues.pagesFrom),
                            Number(selectedValues.pagesTo)
                          )}
                        </span>
                        <button
                          type="button"
                          className={s.selectedTagClose}
                          onClick={() =>
                            onChange({ ...selectedValues, pagesFrom: undefined, pagesTo: undefined })
                          }
                        >
                          <SpriteIcon icon={closeIcon} />
                        </button>
                      </Tag>
                    )}
                    {!!(selectedValues.wordsFrom || selectedValues.wordsTo) && (
                      <Tag className={s.selectedTag}>
                        <span>
                          {getSelectedUnitText(
                            "word",
                            Number(selectedValues.wordsFrom),
                            Number(selectedValues.wordsTo)
                          )}
                        </span>
                        <button
                          type="button"
                          className={s.selectedTagClose}
                          onClick={() =>
                            onChange({ ...selectedValues, wordsFrom: undefined, wordsTo: undefined })
                          }
                        >
                          <SpriteIcon icon={closeIcon} />
                        </button>
                      </Tag>
                    )}
                  </div>
                )}

                {selectedValues.title && (
                  <div className={s.selectedTagsGroup}>
                    <p className={s.selectedTagsHeading}>Title</p>
                    <Tag className={s.selectedTag}>
                      <span>{selectedValues.title}</span>
                      <button
                        type="button"
                        className={s.selectedTagClose}
                        onClick={() => onChange({ ...selectedValues, title: "" })}
                      >
                        <SpriteIcon icon={closeIcon} />
                      </button>
                    </Tag>
                  </div>
                )}
              </div>
            </div>
            <div className={cn(s.filterItems, { [s.filterItemsActive]: filterView })}>
              <div className={s.optionsTitle}>
                {filterView === "volume"
                  ? "Volume"
                  : filterTabs.find((tab) => tab.name === filterView)?.label}
              </div>
              {filterView === "volume" && (
                <WordsPagesInput
                  initialField={selectedValues.wordsFrom || selectedValues.wordsTo ? "words" : "pages"}
                  onTabChange={() =>
                    onChange({
                      ...selectedValues,
                      pagesFrom: undefined,
                      pagesTo: undefined,
                      wordsFrom: undefined,
                      wordsTo: undefined,
                    })
                  }
                  initialWordsValue={{
                    from: selectedValues.wordsFrom ? String(selectedValues.wordsFrom) : undefined,
                    to: selectedValues.wordsTo ? String(selectedValues.wordsTo) : undefined,
                  }}
                  initialPagesValue={{
                    from: selectedValues.pagesFrom ? String(selectedValues.pagesFrom) : undefined,
                    to: selectedValues.pagesTo ? String(selectedValues.pagesTo) : undefined,
                  }}
                  onWordsChange={(from?: number, to?: number) =>
                    onChange({
                      ...selectedValues,
                      wordsFrom: from,
                      wordsTo: to,
                    })
                  }
                  onPagesChange={(from?: number, to?: number) =>
                    onChange({
                      ...selectedValues,
                      pagesFrom: from,
                      pagesTo: to,
                    })
                  }
                />
              )}
              {filterTabs
                .find((tab) => tab.name === filterView)
                ?.options.map((option) => (
                  <button
                    type="button"
                    key={option.name}
                    className={cn(s.filterInnerBtn, s.filterInnerBtnCheckbox, {
                      [s.filterInnerBtnCheckboxActive]:
                        filterView &&
                        (Array.isArray(selectedValues[filterView])
                          ? (selectedValues[filterView] as string[]).includes(`${option.value}`)
                          : option.value.split(",").includes(selectedValues[filterView] as string)),
                    })}
                    onClick={() => filterView && handleOptionClick(filterView, `${option.value}`)}
                  >
                    <span>{option.name}</span>
                  </button>
                ))}
            </div>
          </div>
          <div className={s.bottom}>
            <Button tag="button" type="accent" htmlType="button" fullWidth onClick={onShowResultsButtonClick}>
              Show results ({resultsCount})
            </Button>
          </div>
        </div>
      </Portal>
    </>
  );
};
